import { FC, useCallback, useMemo } from 'react'
import styles from 'components/features/pfm/OpportunitiesPage/OpportunitiesPage.styl'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { OpportunitiesCategoryMenu } from 'components/features/pfm/OpportunitiesCategoryMenu/OpportunitiesCategoryMenu'
import { OPPORTUNITIES_PAGE_PLACEMENT } from 'constants/opportunities'
import Anchor from 'components/atoms/navigation/Anchor'
import { DesktopInternalStickyHeader } from 'components/features/pfm/DesktopInternalStickyHeader/DesktopInternalStickyHeader'
import { connectShadedLoadable, bindGuard } from 'helpers/loadableHelpers'
import { socialInsightsGuard } from 'guards/socialInsightGuards'
import { peergroupsGuard } from 'guards/globalGuards'
import { SocialInsightGroupsList } from 'components/features/socialFeed/SocialInsightGroupsList/SocialInsightGroupsList'
import { SocialInsightList } from 'components/features/socialFeed/SocialInsightList/SocialInsightList'
import { PageHeading } from 'components/atoms/Text'
import { SingleColumnLayout } from 'components/layouts/Layouts'
import { ScrollToTopButton } from 'components/features/pfm/ScrollToTopButton/ScrollToTopButton'
import { useSelectedCategoryId } from 'helpers/hookHelpers'
import { OPPORTUNITIES_PAGE } from 'constants/pages'
import { usePageTitle } from 'hooks/opportunitiesHooks'
import { AdvertiserDisclosure } from 'components/features/pfm/AdvertiserDisclosure/AdvertiserDisclosure'
import { FeedGuard } from 'components/features/socialFeed/FeedGuard/FeedGuard'
import { LazyRender } from 'components/features/pfm/LazyRender/LazyRender'
import { InternalPageRedirect } from 'components/features/pfm/InternalPageRedirect/InternalPageRedirect'

type OpportunitiesPageProps = {}

const OpportunitiesPageComponent: FC<OpportunitiesPageProps> = () => {
  const selectedCategoryId = useSelectedCategoryId(OPPORTUNITIES_PAGE)
  const viewportSegment = useStoreSelector(viewportSegmentSelector)

  const notifications = useMemo(() => (
    <NotificationTrayWrapper
      renderLevel='page'
      isPositionRelative
    />
  ), [])

  const pageTitle = usePageTitle(OPPORTUNITIES_PAGE)

  const categoryMenuChild = useMemo(() => (
    <OpportunitiesCategoryMenu />
  ), [])

  const PageLayout = useCallback<FC>(({ children }) => (
    <>
      <InternalLayoutContainer
        sidebar={ <InternalSidebarContainer /> }
        notifications={ notifications }
        stickyHeader={
          viewportSegment !== 'desktop'
            ? categoryMenuChild
            : undefined
        }
      >
        <Anchor placement='opportunities'>
          <Anchor placement={ OPPORTUNITIES_PAGE_PLACEMENT }>
            { viewportSegment === 'desktop' && (
              <DesktopInternalStickyHeader isShown={ () => true } noAnimation>
                { categoryMenuChild }
              </DesktopInternalStickyHeader>
            )}
            { children }
          </Anchor>
        </Anchor>
      </InternalLayoutContainer>
    </>
  ), [categoryMenuChild, notifications, viewportSegment])

  const scrollToTopButtonChild = useMemo(() => (
    <LazyRender inverse permanent key={ selectedCategoryId }>
      <ScrollToTopButton type={ OPPORTUNITIES_PAGE } />
    </LazyRender>
  ), [selectedCategoryId])

  return (
    <PageLayout>
      <SingleColumnLayout>
        <div className={ styles.header }>
          <PageHeading>
            { pageTitle }
          </PageHeading>
          <div className={ styles.disclosureLinkWrapper }>
            <AdvertiserDisclosure />
          </div>
        </div>
        <div className={ styles.opportunitiesPage }>
          { selectedCategoryId === 'top' && (
            <SocialInsightGroupsList placement={ OPPORTUNITIES_PAGE_PLACEMENT } />
          )}
          { selectedCategoryId !== 'top' && (
            <SocialInsightList placement={ OPPORTUNITIES_PAGE_PLACEMENT } selectedCategoryId={ selectedCategoryId } />
          )}
          { scrollToTopButtonChild }
        </div>
      </SingleColumnLayout>
    </PageLayout>
  )
}

const guards = [
  peergroupsGuard,
  bindGuard(socialInsightsGuard, { placement: OPPORTUNITIES_PAGE_PLACEMENT }),
]

const PageWithGuards = connectShadedLoadable<
{}, {}, OpportunitiesPageProps
>(() => () => ({}), () => ({}), guards)(OpportunitiesPageComponent)

export const OpportunitiesPage: FC = () => (
  <InternalPageRedirect>
    <FeedGuard>
      <PageWithGuards />
    </FeedGuard>
  </InternalPageRedirect>
)
